// ADMIN-only edit of a Criteria Checklist catalog template.
// Mutates CriteriaCatalogItem (the template), NOT the per-deal instance: the
// edit propagates to every deal because instances FK to the catalog by GUID
// and store no text snapshot. Strict v1 scope — only the six author-editable
// copy fields. Code / Tier / Lane / FormSchema / IsActive are immutable here.

import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Badge, Button, FormField, Input, Textarea } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useUpdateCatalogItem } from "@/hooks/dealFlow/useDealFlow";
import type {
  CriteriaCatalogItemDto,
  CriteriaLane,
  CriteriaTier,
} from "@/api/dealFlow/types";

interface CriteriaCatalogEditModalProps {
  open: boolean;
  onClose: () => void;
  catalog: CriteriaCatalogItemDto;
}

const TITLE_MAX = 255;
const ESCALATION_MAX = 64;

const LANE_LABEL: Record<CriteriaLane, string> = {
  A1: "A1 · Underwriter",
  A2: "A2 · Financial",
  A3: "A3 · Legal",
  A4: "A4 · Business",
};

const TIER_LABEL: Record<CriteriaTier, string> = {
  DealKiller: "Deal Killer",
  RedFlag: "Red Flag",
  NeedsImprovement: "Needs Improvement",
};

export function CriteriaCatalogEditModal({
  open,
  onClose,
  catalog,
}: CriteriaCatalogEditModalProps) {
  const toast = useToast();
  const update = useUpdateCatalogItem();

  const [title, setTitle] = useState(catalog.title);
  const [description, setDescription] = useState(catalog.description);
  const [requiredAction, setRequiredAction] = useState(catalog.requiredAction);
  const [escalationPath, setEscalationPath] = useState(catalog.escalationPath ?? "");
  const [isCommonExpected, setIsCommonExpected] = useState(catalog.isCommonExpected);
  const [resolutionGuidance, setResolutionGuidance] = useState(
    catalog.resolutionGuidance ?? "",
  );

  // Re-seed local state when the modal is (re)opened for a row, so a prior
  // unsaved edit on a different item never bleeds across.
  useEffect(() => {
    if (!open) return;
    setTitle(catalog.title);
    setDescription(catalog.description);
    setRequiredAction(catalog.requiredAction);
    setEscalationPath(catalog.escalationPath ?? "");
    setIsCommonExpected(catalog.isCommonExpected);
    setResolutionGuidance(catalog.resolutionGuidance ?? "");
  }, [open, catalog]);

  const titleValid = title.trim().length > 0 && title.trim().length <= TITLE_MAX;
  const descriptionValid = description.trim().length > 0;
  const requiredActionValid = requiredAction.trim().length > 0;
  const escalationValid = escalationPath.trim().length <= ESCALATION_MAX;
  const formValid =
    titleValid && descriptionValid && requiredActionValid && escalationValid;

  const onSave = async () => {
    try {
      await update.mutateAsync({
        id: catalog.id,
        body: {
          title: title.trim(),
          description: description.trim(),
          requiredAction: requiredAction.trim(),
          escalationPath: escalationPath.trim() === "" ? null : escalationPath.trim(),
          isCommonExpected,
          resolutionGuidance:
            resolutionGuidance.trim() === "" ? null : resolutionGuidance.trim(),
        },
      });
      toast.success(`${catalog.code} updated. Changes apply to all deals.`);
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`Edit ${catalog.code}`}
      description="Edits the catalog template and apply to every deal — including in-progress ones."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void onSave()}
            disabled={!formValid || update.isPending}
          >
            {update.isPending ? "Saving…" : "Save changes"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-4">
        <div className="flex flex-wrap items-center gap-space-2">
          <Badge status="draft">{catalog.code}</Badge>
          <span className="text-caption text-gray-mid">
            {LANE_LABEL[catalog.lane]} · {TIER_LABEL[catalog.tier]}
          </span>
        </div>

        <FormField label="Title" required>
          <Input
            value={title}
            maxLength={TITLE_MAX}
            invalid={!titleValid}
            onChange={(e) => setTitle(e.target.value)}
          />
        </FormField>

        <FormField label="Description" required>
          <Textarea
            rows={6}
            value={description}
            invalid={!descriptionValid}
            onChange={(e) => setDescription(e.target.value)}
          />
        </FormField>

        <FormField label="Required action" required>
          <Textarea
            rows={4}
            value={requiredAction}
            invalid={!requiredActionValid}
            onChange={(e) => setRequiredAction(e.target.value)}
          />
        </FormField>

        <FormField
          label="Escalation path"
          helperText="e.g. E2, E2+E4. Leave blank for none."
        >
          <Input
            value={escalationPath}
            maxLength={ESCALATION_MAX}
            invalid={!escalationValid}
            onChange={(e) => setEscalationPath(e.target.value)}
          />
        </FormField>

        <section className="flex flex-col gap-space-2 border border-border-light rounded-md p-space-3">
          <label className="inline-flex items-center gap-space-2 text-small text-onyx">
            <input
              type="checkbox"
              checked={isCommonExpected}
              onChange={(e) => setIsCommonExpected(e.target.checked)}
            />
            Commonly observed (shows the calibration callout on this item)
          </label>
          <FormField label="Callout body shown to analysts when this is commonly observed.">
            <Textarea
              rows={3}
              value={resolutionGuidance}
              disabled={!isCommonExpected}
              placeholder="Calibration guidance — e.g. expected in 30-50% of deals; weigh severity in context."
              onChange={(e) => setResolutionGuidance(e.target.value)}
            />
          </FormField>
        </section>
      </div>
    </Modal>
  );
}
